'use client';
import React, { useState, useCallback, useEffect } from 'react';
import dynamic from 'next/dynamic';

import { MapPin, Map, List, Rss } from 'lucide-react';

import { PANTRY_DATA, type Pantry, type PantryStatus } from '@/lib/pantryData';
import { createClient } from '@/lib/supabase/client';
import PantryDetailPanel from './PantryDetailPanel';
import MapFilters from './MapFilters';
import ReportModal from './ReportModal';
import DonateModal from './DonateModal';
import WelcomeModal from './WelcomeModal';
import PantryListSidebar from './PantryListSidebar';
import FeedView from './FeedView';
import { Toaster } from 'sonner';

const MapView = dynamic(() => import('./MapView'), {
  ssr: false,
  loading: () => (
    <div className="flex-1 flex items-center justify-center bg-[#f0faf4]">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-[#2d7a4f] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-[#2d7a4f] font-medium font-display">Loading map…</p>
      </div>
    </div>
  ),
});

export type FilterStatus = 'lahat' | PantryStatus;

function rowToPantry(row: Record<string, unknown>): Pantry {
  return {
    id: row.id as string,
    pangalan: row.pangalan as string,
    address: row.address as string,
    barangay: row.barangay as string,
    lungsod: row.lungsod as string,
    lat: row.lat != null && row.lat !== '' ? parseFloat(String(row.lat)) : NaN,
    lng: row.lng != null && row.lng !== '' ? parseFloat(String(row.lng)) : NaN,
    status: (row.pantry_status as PantryStatus) ?? 'aktibo',
    items: (row.items as string[]) ?? [],
    oras: row.oras as string,
    contact: row.contact as string,
    nagtatag: row.nagtatag as string,
    hulingUpdate: row.huling_update as string,
    larawan: row.larawan as string | undefined,
    itemAvailability: (row.item_availability as Record<string, import('@/lib/pantryData').AvailabilityLevel>) ?? {},
    facebookUrl: (row.facebook_url as string | undefined) || undefined,
    instagramUrl: (row.instagram_url as string | undefined) || undefined,
    twitterUrl: (row.twitter_url as string | undefined) || undefined,
    messengerUrl: (row.messenger_url as string | undefined) || undefined,
    nearestLandmark: (row.nearest_landmark as string | undefined) || undefined,
    contactPerson: (row.contact_person as string | undefined) || undefined,
    pantryPassword: (row.pantry_password as string | null) ?? undefined,
    description: (row.description as string | null) ?? undefined,
    photoUrl: (row.photo_url as string | null) ?? undefined,
    howItStarted: (row.how_it_started as string | null) ?? undefined,
    scheduleType: ((row.schedule_type as string | null) ?? 'fixed') as 'fixed' | 'varies',
    scheduleDetails: (row.schedule_details as string | null) ?? undefined,
  };
}

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  'aktibo': { label: 'Active', color: 'text-[#16a34a]', bg: 'bg-[#dcfce7]' },
  'mababa-stock': { label: 'Low Stock', color: 'text-[#ca8a04]', bg: 'bg-[#fef9c3]' },
  'walang-stock': { label: 'No Stock', color: 'text-[#dc2626]', bg: 'bg-[#fee2e2]' },
  'hindi-aktibo': { label: 'Closed', color: 'text-[#6b7280]', bg: 'bg-[#f3f4f6]' },
};

export default function MapClientWrapper() {
  const [allPantries, setAllPantries] = useState<Pantry[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPantry, setSelectedPantry] = useState<Pantry | null>(null);
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('lahat');
  const [searchQuery, setSearchQuery] = useState('');
  const [reportingPantry, setReportingPantry] = useState<Pantry | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [donateOpen, setDonateOpen] = useState(false);
  const [donatePantryId, setDonatePantryId] = useState<string | undefined>(undefined);
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [mobileTab, setMobileTab] = useState<'map' | 'list' | 'feed'>('map');
  const [desktopFeedOpen, setDesktopFeedOpen] = useState(false);

  // Show welcome modal on first visit
  useEffect(() => {
    const hasSeenWelcome = sessionStorage.getItem('pantrymap_welcome_seen');
    if (!hasSeenWelcome) {
      setWelcomeOpen(true);
      sessionStorage.setItem('pantrymap_welcome_seen', '1');
    }
  }, []);

  useEffect(() => {
    const supabase = createClient();

    async function loadPantries() {
      setLoading(true);
      const { data, error } = await supabase
        .from('pantries')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('MapClientWrapper: failed to fetch pantries', error.message);
        setAllPantries([...PANTRY_DATA]);
      } else {
        const fromDb = (data ?? [])
          .map(rowToPantry)
          .filter((p) => !isNaN(p.lat) && !isNaN(p.lng));
        if (fromDb.length > 0) {
          setAllPantries(fromDb);
        } else {
          setAllPantries([...PANTRY_DATA]);
        }
      }
      setLoading(false);
    }

    loadPantries();

    const channel = supabase
      .channel('map_pantries_live')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'pantries' },
        (payload) => {
          const newPantry = rowToPantry(payload.new as Record<string, unknown>);
          if (isNaN(newPantry.lat) || isNaN(newPantry.lng)) return;
          setAllPantries((prev) => {
            if (prev.some((p) => p.id === newPantry.id)) return prev;
            return [newPantry, ...prev];
          });
        },
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'pantries' },
        (payload) => {
          const updated = rowToPantry(payload.new as Record<string, unknown>);
          if (isNaN(updated.lat) || isNaN(updated.lng)) return;
          setAllPantries((prev) =>
            prev.map((p) => (p.id === updated.id ? updated : p)),
          );
          setSelectedPantry((prev) =>
            prev && prev.id === updated.id ? updated : prev,
          );
        },
      )
      .on(
        'postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'pantries' },
        (payload) => {
          const deletedId = (payload.old as Record<string, unknown>).id as string;
          setAllPantries((prev) => prev.filter((p) => p.id !== deletedId));
          setSelectedPantry((prev) =>
            prev && prev.id === deletedId ? null : prev,
          );
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const filteredPantries = allPantries.filter((p) => {
    const matchesStatus = filterStatus === 'lahat' || p.status === filterStatus;
    const matchesSearch =
      searchQuery === '' ||
      p.pangalan.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.lungsod.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.barangay.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const handlePinClick = useCallback((pantry: Pantry) => {
    setSelectedPantry(pantry);
    setPanelOpen(true);
  }, []);

  const handleClosePanel = useCallback(() => {
    setPanelOpen(false);
    setTimeout(() => setSelectedPantry(null), 300);
  }, []);

  const handleReport = useCallback((pantry: Pantry) => {
    setReportingPantry(pantry);
  }, []);

  const handleSuggestionSelect = useCallback(
    (pantryId: string) => {
      const pantry = allPantries.find((p) => p.id === pantryId);
      if (pantry) {
        setSelectedPantry(pantry);
        setPanelOpen(true);
      }
    },
    [allPantries],
  );

  const handleStatusUpdate = useCallback((updated: Pantry) => {
    setAllPantries((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
    setSelectedPantry((prev) => (prev && prev.id === updated.id ? updated : prev));
  }, []);

  return (
    <>
      {/* ── MOBILE LAYOUT (lg:hidden) ─────────────────────────────────────── */}
      <div className="lg:hidden flex flex-col h-screen relative overflow-hidden">
        <Toaster position="bottom-right" richColors />

        {/* Map / List / Feed Toggle Tabs */}
        <div className="bg-white border-b border-[#d1ead9] px-4 py-2 flex items-center gap-2 z-20 flex-shrink-0">
          <button
            onClick={() => setMobileTab('map')}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-all flex-1 justify-center ${
              mobileTab === 'map' ?'bg-[#2d7a4f] text-white shadow-sm' :'bg-[#f0faf4] text-[#4b5563] hover:bg-[#e8f5ee]'
            }`}
          >
            <Map size={15} />
            Map
          </button>
          <button
            onClick={() => setMobileTab('list')}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-all flex-1 justify-center ${
              mobileTab === 'list' ?'bg-[#2d7a4f] text-white shadow-sm' :'bg-[#f0faf4] text-[#4b5563] hover:bg-[#e8f5ee]'
            }`}
          >
            <List size={15} />
            List ({filteredPantries.length})
          </button>
          <button
            onClick={() => setMobileTab('feed')}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-all flex-1 justify-center ${
              mobileTab === 'feed' ?'bg-[#2d7a4f] text-white shadow-sm' :'bg-[#f0faf4] text-[#4b5563] hover:bg-[#e8f5ee]'
            }`}
          >
            <Rss size={15} />
            Updates
          </button>
        </div>

        {/* Mobile Content Area */}
        <div className="flex-1 overflow-hidden relative">
          {/* Map Tab */}
          {mobileTab === 'map' && (
            <div className="absolute inset-0">
              {loading ? (
                <div className="flex items-center justify-center h-full bg-[#f0faf4]">
                  <div className="text-center">
                    <div className="w-12 h-12 border-4 border-[#2d7a4f] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-[#2d7a4f] font-medium">Loading pantries…</p>
                  </div>
                </div>
              ) : (
                <MapView
                  pantries={filteredPantries}
                  selectedPantry={selectedPantry}
                  onPinClick={handlePinClick}
                />
              )}
            </div>
          )}

          {/* List Tab */}
          {mobileTab === 'list' && (
            <div className="absolute inset-0 overflow-y-auto bg-white">
              {/* Search/Filter Bar */}
              <div className="px-4 py-3 border-b border-[#d1ead9] bg-[#f8fdf9]">
                <input
                  type="text"
                  placeholder="Hanapin ang pantry…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-[#d1ead9] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2d7a4f]/30 bg-white"
                />
              </div>
              {filteredPantries.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
                  <MapPin size={32} className="text-[#d1ead9] mb-3" />
                  <p className="text-sm text-[#9ca3af]">Walang pantry na nahanap.</p>
                  <p className="text-xs text-[#c4c4c4] mt-1">Subukan ang ibang filter o search.</p>
                </div>
              ) : (
                <ul className="divide-y divide-[#f0faf4]">
                  {filteredPantries.map((pantry) => {
                    const statusCfg = STATUS_CONFIG[pantry.status] ?? STATUS_CONFIG['hindi-aktibo'];
                    const isSelected = selectedPantry?.id === pantry.id;
                    return (
                      <li key={pantry.id}>
                        <button
                          onClick={() => {
                            handlePinClick(pantry);
                            setMobileTab('map');
                          }}
                          className={`w-full text-left px-4 py-4 transition-colors hover:bg-[#f0faf4] focus:outline-none ${
                            isSelected ? 'bg-[#f0faf4] border-l-4 border-[#2d7a4f]' : 'border-l-4 border-transparent'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <h3 className="text-sm font-semibold text-[#1e5c38] leading-snug flex-1">{pantry.pangalan}</h3>
                            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${statusCfg.bg} ${statusCfg.color}`}>
                              {statusCfg.label}
                            </span>
                          </div>
                          <p className="text-xs text-[#6b7280] flex items-center gap-1">
                            <MapPin size={11} className="flex-shrink-0" />
                            {pantry.barangay}, {pantry.lungsod}
                          </p>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}

          {/* Feed Tab */}
          {mobileTab === 'feed' && (
            <div className="absolute inset-0">
              <FeedView onDonate={(pantryId) => {
                setDonatePantryId(pantryId);
                setDonateOpen(true);
              }} />
            </div>
          )}
        </div>

        {/* Mobile Bottom Sheet — overlays the map */}
        {mobileTab === 'map' && (
          <div
            className={`absolute bottom-0 left-0 right-0 z-30 bg-white rounded-t-3xl shadow-2xl border-t border-[#d1ead9] transition-transform duration-300 ease-out ${
              panelOpen ? 'translate-y-0' : 'translate-y-full'
            }`}
            style={{ maxHeight: '52vh' }}
          >
            {selectedPantry && (
              <PantryDetailPanel
                pantry={selectedPantry}
                onClose={handleClosePanel}
                onReport={handleReport}
                onStatusUpdate={handleStatusUpdate}
                onDonate={() => {
                  setDonatePantryId(selectedPantry.id);
                  setDonateOpen(true);
                }}
                isMobile
                allPantries={allPantries}
                onSelectPantry={(p) => { setSelectedPantry(p); setPanelOpen(true); }}
              />
            )}
          </div>
        )}
      </div>

      {/* ── DESKTOP LAYOUT (hidden on mobile) ────────────────────────────── */}
      <div className="hidden lg:flex flex-col h-[calc(100vh-64px)] relative overflow-hidden">
        <Toaster position="bottom-right" richColors />

        {/* Filters Bar */}
        <MapFilters
          filterStatus={filterStatus}
          setFilterStatus={setFilterStatus}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          totalVisible={filteredPantries.length}
          onSuggestionSelect={handleSuggestionSelect}
          pantries={allPantries}
        />

        {/* Map + Sidebar Layout */}
        <div className="flex flex-1 overflow-hidden relative">
          {/* Left Pantry List Sidebar */}
          <PantryListSidebar
            pantries={filteredPantries}
            selectedPantry={selectedPantry}
            onSelectPantry={handlePinClick}
          />

          {/* Map */}
          <div className="flex-1 min-w-0 relative">
            {/* Feed toggle button */}
            <div className="absolute top-3 right-3 z-20">
              <button
                onClick={() => setDesktopFeedOpen((v) => !v)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold shadow-md transition-all ${
                  desktopFeedOpen
                    ? 'bg-[#2d7a4f] text-white'
                    : 'bg-white text-[#2d7a4f] border border-[#d1ead9] hover:bg-[#f0faf4]'
                }`}
              >
                <Rss size={15} />
                Updates
              </button>
            </div>

            {loading ? (
              <div className="flex-1 flex items-center justify-center bg-[#f0faf4] h-full">
                <div className="text-center">
                  <div className="w-12 h-12 border-4 border-[#2d7a4f] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                  <p className="text-[#2d7a4f] font-medium font-display">Loading pantries…</p>
                </div>
              </div>
            ) : (
              <MapView
                pantries={filteredPantries}
                selectedPantry={selectedPantry}
                onPinClick={handlePinClick}
              />
            )}

            {/* Desktop Feed overlay panel */}
            {desktopFeedOpen && (
              <div className="absolute inset-y-0 right-0 w-[380px] xl:w-[420px] bg-white border-l border-[#d1ead9] shadow-xl z-10 flex flex-col">
                <FeedView onDonate={(pantryId) => {
                  setDonatePantryId(pantryId);
                  setDonateOpen(true);
                }} />
              </div>
            )}
          </div>

          {/* Desktop Detail Panel */}
          <div
            className={`flex flex-col bg-white border-l border-[#d1ead9] shadow-xl transition-all duration-300 ease-in-out overflow-hidden ${
              panelOpen && !desktopFeedOpen ? 'w-[380px] xl:w-[420px] opacity-100' : 'w-0 opacity-0'
            }`}
          >
            {selectedPantry && (
              <PantryDetailPanel
                pantry={selectedPantry}
                onClose={handleClosePanel}
                onReport={handleReport}
                onStatusUpdate={handleStatusUpdate}
                onDonate={() => {
                  setDonatePantryId(selectedPantry.id);
                  setDonateOpen(true);
                }}
                allPantries={allPantries}
                onSelectPantry={(p) => { setSelectedPantry(p); setPanelOpen(true); }}
              />
            )}
          </div>
        </div>
      </div>

      {/* Modals (shared) */}
      {reportingPantry && (
        <ReportModal
          pantry={reportingPantry}
          onClose={() => setReportingPantry(null)}
        />
      )}
      {donateOpen && (
        <DonateModal
          preselectedPantryId={donatePantryId}
          onClose={() => {
            setDonateOpen(false);
            setDonatePantryId(undefined);
          }}
        />
      )}
      {welcomeOpen && (
        <WelcomeModal
          onClose={() => setWelcomeOpen(false)}
          onDonate={() => {
            setWelcomeOpen(false);
            setDonateOpen(true);
          }}
          onLogin={() => {
            setWelcomeOpen(false);
            window.dispatchEvent(new CustomEvent('open-login-modal'));
          }}
        />
      )}
    </>
  );
}